import React, { useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, Polygon } from '@react-google-maps/api';
import { messagesForEvacuation } from '../helpers/messages';
import BlockedRoadIcon from '../assets/images/blockedroad_icon.png';
import WUIGoIcon from '../assets/images/wui-go_icon.png'
import './normalUI.css';
import * as am4core from '@amcharts/amcharts4/core';  // Correct way to import am4core
import * as am4charts from '@amcharts/amcharts4/charts';
import { PieChart, PieSeries } from '@amcharts/amcharts4/charts';  // Correct named imports for PieChart and PieSeries
import am4themes_animated from '@amcharts/amcharts4/themes/animated'; // Import theme for animation
import { callGetIntent } from '../helpers/AI_functions';

const firePolygonCoords = [
  { lat: 37.978281, lng: -122.592856 },
  { lat: 37.978577, lng: -122.594487 },
  { lat: 37.977503, lng: -122.596053 },
  { lat: 37.976505, lng: -122.596633 },
  { lat: 37.976624, lng: -122.594766 },
  { lat: 37.976319, lng: -122.593092 },
  { lat: 37.976996, lng: -122.592105 },
];

const availableRoads = [
  "Dominga Avenue",
  "Bolinas Road"
  // Add more roads as needed
];

function NormalUI() {
  const [orderType, setOrderType] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [activeTab, setActiveTab] = useState('map');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [blockedRoads, setBlockedRoads] = useState([]);  // Default value for blocked roads
  const [totalViews] = useState(20000); // Total views (static value for now)
  const [evacuateDirection, setEvacuateDirection] = useState();
  const [timeToEvacuate, setTimeToEvacuate] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [freeText, setFreeText] = useState(''); // For the free-text input
  const [getFireData, setGetFireData] = useState(false); // Checkbox state for getFireData


  useEffect(() => {
    am4core.useTheme(am4themes_animated);

    // Language Pie Chart
    const languageChart = am4core.create('languagePieChart', am4charts.PieChart);
    languageChart.data = [
      { category: 'English', value: 70 },
      { category: 'Spanish', value: 25 },
      { category: 'Mandarin', value: 5 },
    ];
    const languagePieSeries = languageChart.series.push(new am4charts.PieSeries());
    languagePieSeries.dataFields.value = 'value';
    languagePieSeries.dataFields.category = 'category';

    // Planning Tab Opens Pie Chart
    const planningChart = am4core.create('planningPieChart', am4charts.PieChart);
    planningChart.data = [
      { category: 'English', value: 60 },
      { category: 'Spanish', value: 30 },
      { category: 'Mandarin', value: 10 },
    ];
    const planningPieSeries = planningChart.series.push(new am4charts.PieSeries());
    planningPieSeries.dataFields.value = 'value';
    planningPieSeries.dataFields.category = 'category';

    // Planning Popularity Bar Chart
    const planningBarChart = am4core.create('planningBarChart', am4charts.XYChart);
    planningBarChart.data = [
      { category: 'Defensible space inspection', value: 8000 },
      { category: 'Wildfire fuel management', value: 6500 },
      { category: 'Go downhill', value: 4000 },
      { category: 'Garden Hose setup', value: 3800 },
      { category: 'First aid kit', value: 2000 },
      { category: 'Leather gloves', value: 1500 },
      { category: 'Sanitation supplies', value: 1250 },
    ];
    const categoryAxis = planningBarChart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'category';
    categoryAxis.renderer.grid.template.location = 0;
    const valueAxis = planningBarChart.yAxes.push(new am4charts.ValueAxis());
    const barSeries = planningBarChart.series.push(new am4charts.ColumnSeries());
    barSeries.dataFields.valueY = 'value';
    barSeries.dataFields.categoryX = 'category';
    barSeries.columns.template.tooltipText = '{category}: {value} clicks';

    // Drills Per Day Line Chart
    const drillsLineChart = am4core.create('drillsLineChart', am4charts.XYChart);
    drillsLineChart.data = [
      { date: 'Monday', value: 100 },
      { date: 'Tuesday', value: 100 },
      { date: 'Wednesday', value: 100 },
      { date: 'Thursday', value: 400 },
      { date: 'Friday', value: 1000 },
    ];
    const dateAxis = drillsLineChart.xAxes.push(new am4charts.CategoryAxis());
    dateAxis.dataFields.category = 'date';
    dateAxis.renderer.grid.template.location = 0;

    const drillsValueAxis = drillsLineChart.yAxes.push(new am4charts.ValueAxis());

    const lineSeries = drillsLineChart.series.push(new am4charts.LineSeries());
    lineSeries.dataFields.valueY = 'value';
    lineSeries.dataFields.categoryX = 'date';
    lineSeries.strokeWidth = 3;

    // Enable tooltips properly
    lineSeries.tooltipText = '{date}: {value} drills';
    lineSeries.tooltip.getFillFromObject = true; 

    // Enable cursor for interaction
    drillsLineChart.cursor = new am4charts.XYCursor();
    drillsLineChart.cursor.behavior = 'panXY';
    drillsLineChart.cursor.xAxis = dateAxis;
    drillsLineChart.cursor.snapToSeries = lineSeries;

    return () => {
      languageChart.dispose();
      planningChart.dispose();
      planningBarChart.dispose();
      drillsLineChart.dispose();
    };
  }, []);

  const handleRoadSelect = (road) => {
    if (!blockedRoads.includes(road)) {
      setBlockedRoads([...blockedRoads, road]);
    }
  };

  const handleRoadRemove = (road) => {
    setBlockedRoads(blockedRoads.filter((item) => item !== road));
  };

  const handleSave = () => {
    if (!orderType || !evacuateDirection || !timeToEvacuate) {
      alert('Please fill in all fields.');
      return;
    }
    
    // Map the orderType to the required value
    const orderTypeMap = {
      evacuation_warning: 'evac_warning',
      evacuation_order: 'evac_order',
      shelter_in_place: 'shelter'
    };

    // Get the mapped orderType or use the original if not found
    const mappedOrderType = orderTypeMap[orderType] || orderType;
    
    // If there are blocked roads, add to the generated link
    const blockedParam = blockedRoads.length > 0 ? '&blocked=7001' : '';  // Add the blocked parameter
    const link = `https://wuigoapp?latitude=${latitude}&longitude=${longitude}&ord=${mappedOrderType}${blockedParam}&tte=${timeToEvacuate}&evac_dir=${evacuateDirection}`;

    setGeneratedLink(link);
  };

  // Handle changes to the blocked roads multi-select dropdown
  const handleBlockedRoadsChange = (e) => {
    const selectedRoads = Array.from(e.target.selectedOptions, option => option.value);
    setBlockedRoads(selectedRoads);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedMessage(null); // Reset selected message when switching tabs
  };

  const mapContainerStyle = {
    width: '100%',
    height: '800px',
  };

  const center =
    latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : { lat: 0, lng: 0 };

  const handleSelectMessage = (key) => {
    setSelectedMessage(messagesForEvacuation[key]);
  };

  const handleFreeTextSubmit = async () => {
    if (!freeText) {
      alert('Please enter a message in the free-text input.');
      return;
    }
  
    const currentState = {
      orderType,
      latitude,
      longitude,
      evacuateDirection,
      timeToEvacuate,
      blockedRoads,
    };
  
    try {
      const response = await callGetIntent(freeText, currentState); // Call your GenAI API

      // Extract the final JSON from the response
      const updatedStateString = response?.intent?.content;

      if (!updatedStateString) {
        console.error('Invalid response format: Missing updated state.');
        alert('An error occurred while processing the input. Please try again.');
        return;
      }

      // Convert the string to an object
      const updatedState = JSON.parse(updatedStateString);

      setOrderType(updatedState.orderType || orderType); // Update state based on the response
      setLatitude(updatedState.latitude || latitude);
      setLongitude(updatedState.longitude || longitude);
      setEvacuateDirection(updatedState.evacuateDirection || evacuateDirection);
      setTimeToEvacuate(updatedState.timeToEvacuate || timeToEvacuate);
      setBlockedRoads(updatedState.blockedRoads || blockedRoads);
    } catch (error) {
      console.error('Error processing free-text input:', error);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <img src={WUIGoIcon} alt="WUI-Go Icon" style={{ width: '300px', height: '120px' }} />
      <br />
      <div style={styles.formContainer}>
        {/* Free Text Input */}
        <textarea
          value={freeText}
          onChange={(e) => setFreeText(e.target.value)}
          rows="4"
          cols="60"
          placeholder="Input your situation. Eg: 'Evacuation order. Evacuate north within 5 minutes. Dominga Avenue is blocked'"
          style={styles.textarea}
        />
        <button onClick={handleFreeTextSubmit} style={styles.button}>Use AI to fill inputs</button>

        <br/>
        <br/>
        <br/>

        {/* Order Type Dropdown */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Order Type:</label>
          <select
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            style={styles.select}
          >
            <option value="">Select...</option>
            <option value="evacuation_warning">Evacuation Warning</option>
            <option value="evacuation_order">Evacuation Order</option>
            <option value="shelter_in_place">Shelter in Place</option>
          </select>
        </div>
        <br/>

        {/* Fire Data Checkbox */}
        <div style={styles.formGroup}>
          <label style={styles.label}>
            <input
              type="checkbox"
              checked={getFireData}
              onChange={(e) => setGetFireData(e.target.checked)}
              style={styles.checkbox}
            />
            Get fire data from integrated sources
          </label>
        </div>
        <br/>

        {/* Blocked Roads Dropdown */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Blocked Roads:</label>
          <div style={styles.dropdownContainer}>
            <div style={styles.dropdownList}>
              {availableRoads.map((road) => (
                <button
                  key={road}
                  onClick={() => handleRoadSelect(road)}
                  style={{
                    ...styles.dropdownItem,
                    backgroundColor: blockedRoads.includes(road) ? '#007BFF' : '#f1f1f1',
                    color: blockedRoads.includes(road) ? 'white' : '#333',
                  }}
                >
                  {road}
                </button>
              ))}
            </div>
            <div style={styles.selectedBadgesContainer}>
              {blockedRoads.map((road) => (
                <span key={road} style={styles.badge}>
                  {road}
                  <button
                    onClick={() => handleRoadRemove(road)}
                    style={styles.badgeCloseButton}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <br/>

        {/* Evacuation Direction Dropdown */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Direction to avoid:</label>
          <select
            value={evacuateDirection}
            onChange={(e) => setEvacuateDirection(e.target.value)}
            style={styles.select}
          >
            <option value="">Select...</option>
            <option value="north">North</option>
            <option value="west">West</option>
          </select>
        </div>

        <br/>

        {/* Time to Evacuate Input */}
        <div style={styles.formGroup}>
          <label style={styles.label}>Time to Evacuate (minutes):</label>
          <input
            type="number"
            value={timeToEvacuate}
            onChange={(e) => setTimeToEvacuate(e.target.value)}
            style={styles.input}
          />
        </div>

        <button onClick={handleSave} style={styles.saveButton}>Save</button>

        {generatedLink && (
        <div style={styles.generatedLink}>
          <strong>Generated Link:   </strong>
          <a href={generatedLink} target="_blank" rel="noopener noreferrer" style={styles.link}>
            {generatedLink}
          </a>
        </div>
      )}
      </div>

      


      {/* Map Section */}
      <div style={{ marginTop: '50px' }}>
        <h2>Map</h2>
        <LoadScript googleMapsApiKey="AIzaSyASU8wb6swDIo3dNxkyjfraWijCImc3-OU">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={{ lat: 37.982253, lng: -122.590436 }} // Default center coordinates
            zoom={16}
          >
            {latitude && longitude && <Marker position={center} />}

            {/* Show blocked road icon if "Dominga Avenue" is selected */}
            {blockedRoads.includes('Dominga Avenue') && (
              <Marker
                position={{ lat: 37.985559, lng: -122.588716 }}
                icon={BlockedRoadIcon}  // Assuming BlockedRoadIcon is properly imported
              />
            )}

            {/* Add Fire Polygon */}
            {getFireData && (
              <Polygon
              paths={firePolygonCoords}
              options={{
                fillColor: 'red',
                fillOpacity: 0.35,
                strokeColor: 'red',
                strokeOpacity: 1,
                strokeWeight: 2,
              }}
            />)}
          </GoogleMap>
        </LoadScript>
      </div>

      

      {/* Statistics Section */}
      <div style={{ marginTop: '20px' }}>
        <h2>Statistics</h2>
        <div>
          <strong>Total Emergency Message opens: </strong>
          <span>{totalViews}</span>
        </div>
        <div style={{ width: '100%', height: '400px' }} id="languagePieChart"></div>
      </div>

      {/* Preparedness Section */}
      <div style={{ marginTop: '40px' }}>
        <h2>Preparedness</h2>
        <h4>Top Planning section opens</h4>
        <div style={{ width: '100%', height: '400px' }} id="planningBarChart"></div>
        <br/>
        <h4>Drills done per day</h4>
        <div style={{ width: '100%', height: '400px' }} id="drillsLineChart"></div>
      </div>
    </div>
  );
}

//EVACUATE NOW! MANDATORY evacuations for [LOCATION] due to wildfire at [LOCATION].
//Dominga Avenue is blocked due to fire.

// Styles
const styles = {
  container: {
    padding: '40px',
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#F9F9F9',
    color: '#333',
  },
  header: {
    fontSize: '36px',
    marginBottom: '20px',
  },
  formContainer: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    marginBottom: '40px',
  },
  formGroup: {
    marginBottom: '15px',
  },
  label: {
    display: 'block',
    fontWeight: 'bold',
    marginBottom: '5px',
  },
  select: {
    width: '300px',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
  },
  input: {
    width: '275px',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
  },
  button: {
    backgroundColor: '#007BFF',
    color: '#fff',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
  },
  saveButton: {
    backgroundColor: '#28A745',
    color: '#fff',
    padding: '12px 20px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '16px',
    marginTop: '20px',
  },
  textarea: {
    width: '95%',
    padding: '10px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '16px',
    resize: 'vertical',
  },
  generatedLink: {
    marginTop: '20px',
  },
  link: {
    color: '#007BFF',
    textDecoration: 'none',
  },
  mapContainer: {
    marginTop: '50px',
  },
  subHeader: {
    fontSize: '24px',
    marginBottom: '20px',
  },
  statisticsContainer: {
    marginTop: '30px',
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
  },
  statisticsItem: {
    marginBottom: '10px',
    fontSize: '18px',
  },
  dropdownContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  dropdownList: {
    marginBottom: '10px',
  },
  dropdownItem: {
    display: 'block',
    padding: '10px',
    marginBottom: '5px',
    backgroundColor: '#f1f1f1',
    border: '1px solid #ccc',
    borderRadius: '5px',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '16px',
    width: '300px',
  },
  selectedBadgesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
  },
  badge: {
    backgroundColor: '#007BFF',
    color: '#fff',
    padding: '5px 10px',
    borderRadius: '15px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '10px',
  },
  badgeCloseButton: {
    background: 'none',
    border: 'none',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
    marginLeft: '8px',
  },
};

export default NormalUI;


/*
<div style={{ marginTop: '40px' }}>
<h2>Messaging</h2>
<div style={{ padding: '20px' }}>
  {!selectedMessage ? (
      <ul>
        {Object.keys(messagesForEvacuation).map((key) => (
          <li
            key={key}
            onClick={() => handleSelectMessage(key)}
            style={{
              cursor: 'pointer',
              padding: '10px',
              borderBottom: '1px solid #ddd',
            }}
          >
            <strong>{messagesForEvacuation[key].scenario_title.EN}</strong>
            <br />
            <small>{messagesForEvacuation[key].message.EN}</small>
          </li>
        ))}
      </ul>
    ) : (
      <div>
        <button
          onClick={() => setSelectedMessage(null)}
          style={{
            marginBottom: '10px',
            padding: '5px 10px',
            background: '#ddd',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Back to list
        </button>
        <h2>{selectedMessage.scenario_title.EN}</h2>
        <p><strong>Source Agency:</strong> {selectedMessage.source_agency}</p>
        <p><strong>Message:</strong> {selectedMessage.message.EN}</p>
        <p><strong>Impact:</strong> {selectedMessage.impact.EN}</p>
        <p><strong>Nudge:</strong> {selectedMessage.nudge.EN}</p>
        <p>
          <strong>Utilized Biases:</strong>{' '}
          {selectedMessage.utilized_biases_list.join(', ')}
        </p>
      </div>
    )}
</div>
</div>

<div style={{ marginTop: '40px' }}>
<h2>Enter Message for AI</h2>
<textarea
  value={userMessage}
  onChange={(e) => setUserMessage(e.target.value)}
  rows="4"
  cols="50"
  placeholder="Type your message here..."
></textarea>
<br />
<button
  onClick={handleMessageSubmit}
  style={{ padding: '10px 15px', marginTop: '10px' }}
>
  Send Message
</button>

{aiResponse && (
  <div style={{ marginTop: '20px' }}>
    <h3>AI Response</h3>
    <p>{aiResponse}</p>
  </div>
)}
</div>
*/

/* 

 {!getFireData && (
  <>
    <div style={styles.formGroup}>
      <label style={styles.label}>Latitude:</label>
      <input
        type="text"
        value={latitude}
        onChange={(e) => setLatitude(e.target.value)}
        style={styles.input}
      />
    </div>
    <div style={styles.formGroup}>
      <label style={styles.label}>Longitude:</label>
      <input
        type="text"
        value={longitude}
        onChange={(e) => setLongitude(e.target.value)}
        style={styles.input}
      />
    </div>
  </>
)}
*/
